import React, { useState, useEffect } from "react";
import { Drawer, Button, Checkbox, Tag, Typography, Space } from "antd";
import { ExportOutlined } from "@ant-design/icons";

const isH = (order) => String(order.daily_id).toUpperCase().startsWith("H");

const isUnassigned = (order, routeDay) => {
  const entry = order.delivery_info_array?.find((d) =>
    d.delivery_date?.startsWith(routeDay),
  );
  return !entry?.driver_id;
};

const OrderSelectorDrawer = ({
  open,
  onClose,
  orders,
  routeDay,
  selectedIds,
  onApply,
  drivers = [],
}) => {
  const [localSelected, setLocalSelected] = useState(new Set(selectedIds));

  useEffect(() => {
    if (open) setLocalSelected(new Set(selectedIds));
  }, [open]);

  const toggle = (orderId) =>
    setLocalSelected((prev) => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });

  const addToSelection = (predicate) =>
    setLocalSelected(
      (prev) =>
        new Set([...prev, ...orders.filter(predicate).map((o) => o.order_id)]),
    );

  const resolveDriverName = (delivery) => {
    if (!delivery?.driver_id) return null;
    if (delivery.driver_name) return delivery.driver_name;
    return (
      drivers.find((d) => d.personnel_id === delivery.driver_id)?.name ??
      `ID ${delivery.driver_id}`
    );
  };

  const hCount = orders.filter(isH).length;
  const unassignedCount = orders.filter((o) =>
    isUnassigned(o, routeDay),
  ).length;

  const handleApply = () => {
    onApply(localSelected);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Pedidos del día — ${routeDay}`}
      width={640}
      footer={
        <div className="flex justify-between items-center">
          <Typography.Text type="secondary">
            {localSelected.size} / {orders.length} seleccionados
          </Typography.Text>
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" onClick={handleApply}>
              Aplicar selección
            </Button>
          </Space>
        </div>
      }
    >
      {/* Quick-select toolbar */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={() => addToSelection(isH)}>
          + Pedidos "H" <Tag>{hCount}</Tag>
        </Button>
        <Button
          onClick={() => addToSelection((o) => isUnassigned(o, routeDay))}
        >
          + No asignados <Tag>{unassignedCount}</Tag>
        </Button>
        <Button
          onClick={() =>
            setLocalSelected(new Set(orders.map((o) => o.order_id)))
          }
        >
          Todos
        </Button>
        <Button onClick={() => setLocalSelected(new Set())}>Limpiar</Button>
      </div>

      {/* Order list */}
      <div
        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-y-auto"
        style={{ maxHeight: "calc(100vh - 220px)" }}
      >
        {orders.map((order) => {
          const checked = localSelected.has(order.order_id);
          const deliveries = order.delivery_info_array ?? [];
          const todayDelivery = deliveries.find((d) =>
            d.delivery_date?.startsWith(routeDay),
          );
          const driverName = resolveDriverName(todayDelivery);

          return (
            <div
              key={order.order_id}
              onClick={() => toggle(order.order_id)}
              className={`px-4 py-2 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                checked
                  ? "bg-blue-50 dark:bg-blue-950/30"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              <div className="flex justify-between items-start gap-3">
                {/* Left: checkbox + meta */}
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <Checkbox
                    checked={checked}
                    className="mt-0.5 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(order.order_id)}
                  />
                  <div className="flex flex-col gap-0.5 min-w-0">
                    {/* Row 1: id + H badge + name */}
                    <Space size={4} wrap>
                      <Tag className="font-mono" style={{ fontSize: 11 }}>
                        {order.daily_id}
                      </Tag>
                      {isH(order) && (
                        <Tag
                          color="blue"
                          style={{ fontSize: 10, padding: "0 4px" }}
                        >
                          H
                        </Tag>
                      )}
                      <Typography.Text strong className="text-sm">
                        {order.name}
                      </Typography.Text>
                    </Space>

                    {/* Row 2: address */}
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 11 }}
                      className="block"
                    >
                      {[order.address, order.city?.toUpperCase()]
                        .filter(Boolean)
                        .join(" — ")}
                    </Typography.Text>

                    {/* Row 3: products stacked */}
                    {order.products?.length > 0 && (
                      <div className="flex flex-col gap-0.5 mt-0.5">
                        {order.products.map((p, i) => (
                          <Tag
                            key={i}
                            style={{
                              fontSize: 11,
                              padding: "0 5px",
                              width: "fit-content",
                            }}
                          >
                            {p.quantity}× {p.product_name}
                            {p.variant ? ` (${p.variant})` : ""}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: driver + open link */}
                <div
                  className="flex flex-col items-end gap-1 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {driverName ? (
                    <Tag color="green" style={{ fontSize: 11 }}>
                      {driverName}
                    </Tag>
                  ) : (
                    <Tag color="orange" style={{ fontSize: 11 }}>
                      Sin chofer
                    </Tag>
                  )}
                  <Button
                    icon={<ExportOutlined />}
                    size="small"
                    type="text"
                    onClick={() =>
                      window.open(`/orders/${order.order_id}`, "_blank")
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};

export default OrderSelectorDrawer;
